// Geometry helpers for turning a closed loop of free-form walls (any angle +
// curve) into a room outline with a floor. Coordinates are in grid-cell units
// (the same units freeWalls use). Curves are quadratic arcs whose sagitta is
// `bulge` cells off the chord midpoint (see perp-unit convention below).

// Perp unit for the +bulge side of an edge a→b: (dy, -dx)/len. The arc passes
// through chordMid + perp·bulge, so the quadratic control point is
// chordMid + perp·(2·bulge).
export function edgeControlPoint(ax, ay, bx, by, bulge) {
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const nx = dy / len, ny = -dx / len;
  return { cx: (ax + bx) / 2 + nx * bulge * 2, cy: (ay + by) / 2 + ny * bulge * 2 };
}

// Order a mixed set of walls (each { id, x1,y1,x2,y2, bulge? } — grid segments
// pass bulge 0) into ONE closed polygon by matching shared endpoints (within
// `tol`). Walls that TOUCH the loop but aren't part of it are ignored (so an
// outline can include extra loose walls). Returns:
//   { loop: [{x,y,bulge}], usedIds: [...] }   — bulge is the curve of the edge
//     FROM this vertex to the next (direction-corrected); loop wraps last→first
//   null — the walls don't form a closed loop.
//
// A junction where three or more walls meet is explored EXHAUSTIVELY (depth
// first, with backtracking) rather than by taking the first endpoint that
// matches. Greedy matching both missed loops — one wrong turn dead-ended and the
// search gave up — and, now that the walls of existing ROOMS are candidates too
// (so a new room can close against a room already standing), could return a loop
// wrapping several rooms instead of the one just enclosed. Of every loop found,
// the SMALLEST by area is the face the user actually closed.
//
// opts:
//   tol       — how close two endpoints must be to count as joined, in cells
//   requireId — only consider loops running through this wall (the one just drawn)
//   accept    — extra predicate on the used ids, e.g. "must use a wall the user drew"
//   minArea   — reject slivers (cells²)
//   maxSteps  — hard bound on the search
export function chainFreeWallsToLoop(walls, opts = {}) {
  const { tol = 0.7, requireId = null, accept = null, minArea = 0.5, maxSteps = 20000 } = opts;
  const segs = (walls || []).filter(w => Math.hypot(w.x2 - w.x1, w.y2 - w.y1) > 0.05);
  if (segs.length < 3) return null; // need ≥3 edges to enclose an area

  const near = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by) <= tol;

  let steps = 0;
  let best = null, bestArea = Infinity;

  const record = (loop, usedIds) => {
    if (loop.length < 3) return;
    if (accept && !accept(usedIds)) return;
    const area = Math.abs(loopSignedArea(loop));
    if (area < minArea || area >= bestArea) return;
    bestArea = area;
    best = { loop: loop.map(p => ({ ...p })), usedIds: [...usedIds] };
  };

  // `loop` carries one vertex per wall walked: where that wall STARTS, holding
  // that wall's direction-corrected bulge.
  const walk = (startX, startY, curX, curY, loop, usedIds, used) => {
    if (steps++ > maxSteps) return;
    if (loop.length >= 3 && near(curX, curY, startX, startY)) { record(loop, usedIds); return; }
    for (const w of segs) {
      if (used.has(w.id)) continue;
      let forward;
      if (near(w.x1, w.y1, curX, curY)) forward = true;
      else if (near(w.x2, w.y2, curX, curY)) forward = false;
      else continue;
      used.add(w.id); usedIds.push(w.id);
      loop.push({ x: curX, y: curY, bulge: forward ? (w.bulge || 0) : -(w.bulge || 0) });
      walk(startX, startY, forward ? w.x2 : w.x1, forward ? w.y2 : w.y1, loop, usedIds, used);
      loop.pop(); usedIds.pop(); used.delete(w.id);
    }
  };

  // Seed from the wall that has to be in the loop; otherwise from each in turn,
  // since the first wall might be a loose one that's in no loop at all.
  const seeds = requireId ? segs.filter(w => w.id === requireId) : segs;
  for (const seed of seeds) {
    walk(
      seed.x1, seed.y1, seed.x2, seed.y2,
      [{ x: seed.x1, y: seed.y1, bulge: seed.bulge || 0 }],
      [seed.id], new Set([seed.id]),
    );
  }
  return best;
}

// Point + tangent angle at parameter t∈[0,1] along the edge from vertex `a` to
// `b` (quadratic arc when a.bulge ≠ 0, straight line otherwise). Used to place
// openings (doors/windows) on a free room's wall in both 2D and 3D.
export function edgePointAt(a, b, t) {
  const bulge = a.bulge || 0;
  if (!bulge) {
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      angle: Math.atan2(b.y - a.y, b.x - a.x),
    };
  }
  const { cx, cy } = edgeControlPoint(a.x, a.y, b.x, b.y, bulge);
  const mt = 1 - t;
  const x = mt * mt * a.x + 2 * mt * t * cx + t * t * b.x;
  const y = mt * mt * a.y + 2 * mt * t * cy + t * t * b.y;
  // derivative of the quadratic → tangent
  const dx = 2 * mt * (cx - a.x) + 2 * t * (b.x - cx);
  const dy = 2 * mt * (cy - a.y) + 2 * t * (b.y - cy);
  return { x, y, angle: Math.atan2(dy, dx) };
}

// Approximate length (in cells) of the edge a→b, sampling the arc when curved.
export function edgeLength(a, b) {
  if (!a.bulge) return Math.hypot(b.x - a.x, b.y - a.y);
  let len = 0, prev = edgePointAt(a, b, 0);
  for (let i = 1; i <= 16; i++) {
    const p = edgePointAt(a, b, i / 16);
    len += Math.hypot(p.x - prev.x, p.y - prev.y);
    prev = p;
  }
  return len;
}

// Flatten a free-form outline (straight + curved edges) into a dense polygon of
// plain {x,y} points — the basis for area tests and rasterising.
export function flattenLoop(pts, perEdge = 24) {
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const steps = a.bulge ? perEdge : 1;
    for (let s = 0; s < steps; s++) out.push(edgePointAt(a, b, s / steps));
  }
  return out;
}

// Even-odd point-in-polygon test on a flattened outline.
export function pointInPolygon(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-9) + xi) inside = !inside;
  }
  return inside;
}

// Convert a free-form outline into the RECT LIST the room system is built on:
// rasterise the shape onto the 1-cell grid, then merge each row's covered cells
// into horizontal runs. This is what lets a curved/angled room become a REAL
// room (`placePolygonRoom`) so every existing room tool works on it; the smooth
// `pts` outline is kept alongside purely for rendering.
export function polygonToRects(pts, gridSize = 400) {
  const poly = flattenLoop(pts);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of poly) {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  }
  const c0 = Math.max(0, Math.floor(minX)), c1 = Math.min(gridSize, Math.ceil(maxX));
  const r0 = Math.max(0, Math.floor(minY)), r1 = Math.min(gridSize, Math.ceil(maxY));
  const rects = [];
  for (let r = r0; r < r1; r++) {
    let runStart = null;
    for (let c = c0; c <= c1; c++) {
      // A cell belongs to the room when its centre is inside the outline.
      const inside = c < c1 && pointInPolygon(poly, c + 0.5, r + 0.5);
      if (inside && runStart === null) runStart = c;
      if (!inside && runStart !== null) {
        rects.push({ x: runStart, y: r, w: c - runStart, h: 1 });
        runStart = null;
      }
    }
  }
  return rects;
}

// ─── POLYGON UNION ───────────────────────────────────────────────────────────
// Merging a free-form room with rectangular rooms needs ONE combined outline,
// otherwise 3D (which can't union two shapes the way SVG fills can) shows only
// part of the room. Curves are handled by densifying first: the union comes back
// as a dense point list, which still reads as a smooth curve when rendered.

const UEPS = 0.02;   // "on the boundary" tolerance, in cells

function distToPolyBoundary(poly, x, y) {
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const dx = b.x - a.x, dy = b.y - a.y;
    const L2 = dx * dx + dy * dy;
    let t = L2 ? ((x - a.x) * dx + (y - a.y) * dy) / L2 : 0;
    t = Math.max(0, Math.min(1, t));
    best = Math.min(best, Math.hypot(x - (a.x + dx * t), y - (a.y + dy * t)));
  }
  return best;
}

// Split a closed dense polygon into the maximal runs of points that pass `keep`.
function boundaryRuns(poly, keep) {
  const flags = poly.map(p => keep(p));
  if (flags.every(Boolean)) return [poly.slice()];
  if (!flags.some(Boolean)) return [];
  // Rotate so index 0 starts a run, then collect consecutive kept stretches.
  let start = flags.findIndex((f, i) => f && !flags[(i - 1 + flags.length) % flags.length]);
  if (start < 0) start = 0;
  const runs = [];
  let cur = null;
  for (let k = 0; k < poly.length; k++) {
    const i = (start + k) % poly.length;
    if (flags[i]) { (cur = cur || []).push(poly[i]); }
    else if (cur) { runs.push(cur); cur = null; }
  }
  if (cur) runs.push(cur);
  return runs;
}

// Chain open polylines into one closed loop by repeatedly hopping to whichever
// remaining run starts (or ends) nearest the current end point.
function chainRuns(runs) {
  if (!runs.length) return [];
  const left = runs.slice();
  const out = left.shift().slice();
  while (left.length) {
    const end = out[out.length - 1];
    let bi = -1, bd = Infinity, rev = false;
    for (let i = 0; i < left.length; i++) {
      const r = left[i];
      const dS = Math.hypot(r[0].x - end.x, r[0].y - end.y);
      const dE = Math.hypot(r[r.length - 1].x - end.x, r[r.length - 1].y - end.y);
      if (dS < bd) { bd = dS; bi = i; rev = false; }
      if (dE < bd) { bd = dE; bi = i; rev = true; }
    }
    // A genuine continuation joins where the previous run ended. A far-away
    // "nearest" run belongs to a separate piece — stop rather than draw a
    // connector across the middle of the room.
    if (bi < 0 || bd > 1.5) break;
    const seg = left.splice(bi, 1)[0];
    out.push(...(rev ? seg.slice().reverse() : seg));
  }
  return out;
}

// Union of two dense closed polygons. Shared (collinear) edges — which happen
// whenever two rooms are merged along a wall — are resolved by keeping P's copy
// (points ON the other boundary count as kept for P, dropped for Q), so the seam
// doesn't produce a gap or a doubled edge.
export function unionPolygons(P, Q) {
  // A point stays on the union boundary only if it is STRICTLY outside the other
  // polygon. Points lying ON the other boundary are dropped from BOTH — that
  // stretch is a shared wall, i.e. interior to the union. Dropping it leaves two
  // open runs whose endpoints meet, which chain into the correct outline.
  // (Keeping them produced a closed loop per polygon, and chaining those two
  // closed loops drew diagonal connectors straight across the room.)
  const outsideQ = (p) => !pointInPolygon(Q, p.x, p.y) && distToPolyBoundary(Q, p.x, p.y) > UEPS;
  const outsideP = (p) => !pointInPolygon(P, p.x, p.y) && distToPolyBoundary(P, p.x, p.y) > UEPS;
  const runsP = boundaryRuns(P, outsideQ);
  const runsQ = boundaryRuns(Q, outsideP);
  if (!runsP.length) return Q.slice();    // P swallowed by Q
  if (!runsQ.length) return P.slice();    // Q swallowed by P (or only touching)
  const loop = chainRuns([...runsP, ...runsQ]);
  return loop.length >= 3 ? loop : P.slice();
}

// Densify a rectangle into a point loop so unions with it stay well-conditioned.
function rectToPoly(r, step = 0.25) {
  const pts = [];
  const push = (x, y) => pts.push({ x, y });
  const edge = (x1, y1, x2, y2) => {
    const n = Math.max(1, Math.ceil(Math.hypot(x2 - x1, y2 - y1) / step));
    for (let i = 0; i < n; i++) push(x1 + (x2 - x1) * i / n, y1 + (y2 - y1) * i / n);
  };
  edge(r.x, r.y, r.x + r.w, r.y);
  edge(r.x + r.w, r.y, r.x + r.w, r.y + r.h);
  edge(r.x + r.w, r.y + r.h, r.x, r.y + r.h);
  edge(r.x, r.y + r.h, r.x, r.y);
  return pts;
}

// Merge a free-form outline with a set of rectangles into ONE outline, returned
// in the `pts` format ({x,y,bulge}).
//
// Implemented as a signed-distance contour rather than by stitching boundary
// pieces together: each shape contributes a field that is positive inside and
// negative outside, the union is their max, and the zero-contour is traced with
// interpolated marching squares. That is topologically robust for every
// configuration (shared wall, overlap, containment, several rectangles) — the
// stitching approach produced self-intersections and dropped pieces on exactly
// those cases — and the interpolation keeps curves smooth rather than stepped.
export function unionFreeShape(pts, rects) {
  if (!pts || pts.length < 3) return pts;
  const poly = flattenLoop(pts, 48);
  const boxes = (rects || []).filter(r => r && r.w > 0 && r.h > 0);
  if (!boxes.length) return pts;

  // Signed distance: + inside, − outside.
  const sdPoly = (x, y) => (pointInPolygon(poly, x, y) ? 1 : -1) * distToPolyBoundary(poly, x, y);
  const sdRect = (r, x, y) => {
    const dx = Math.min(x - r.x, r.x + r.w - x);
    const dy = Math.min(y - r.y, r.y + r.h - y);
    if (dx > 0 && dy > 0) return Math.min(dx, dy);                 // inside
    return -Math.hypot(Math.max(0, -dx), Math.max(0, -dy));        // outside
  };
  const field = (x, y) => {
    let v = sdPoly(x, y);
    for (const r of boxes) v = Math.max(v, sdRect(r, x, y));
    return v;
  };

  // Sample grid over the combined bounds, with a margin so the contour closes.
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of poly) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
  for (const r of boxes) { minX = Math.min(minX, r.x); maxX = Math.max(maxX, r.x + r.w); minY = Math.min(minY, r.y); maxY = Math.max(maxY, r.y + r.h); }
  const STEP = 0.2, PAD = 0.6;
  minX -= PAD; minY -= PAD; maxX += PAD; maxY += PAD;
  const nx = Math.max(2, Math.ceil((maxX - minX) / STEP));
  const ny = Math.max(2, Math.ceil((maxY - minY) / STEP));
  const gx = i => minX + (i * (maxX - minX)) / nx;
  const gy = j => minY + (j * (maxY - minY)) / ny;
  const F = [];
  for (let j = 0; j <= ny; j++) { const row = []; for (let i = 0; i <= nx; i++) row.push(field(gx(i), gy(j))); F.push(row); }

  // Marching squares, collecting zero-crossing segments with linear interpolation.
  const lerp = (p, q, a, b) => { const t = a / (a - b); return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t }; };
  const segs = [];
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const P = [{ x: gx(i), y: gy(j) }, { x: gx(i + 1), y: gy(j) }, { x: gx(i + 1), y: gy(j + 1) }, { x: gx(i), y: gy(j + 1) }];
      const V = [F[j][i], F[j][i + 1], F[j + 1][i + 1], F[j + 1][i]];
      const cross = [];
      for (let e = 0; e < 4; e++) {
        const a = V[e], b = V[(e + 1) % 4];
        if ((a >= 0) !== (b >= 0)) cross.push(lerp(P[e], P[(e + 1) % 4], a, b));
      }
      if (cross.length === 2) segs.push([cross[0], cross[1]]);
      else if (cross.length === 4) { segs.push([cross[0], cross[1]]); segs.push([cross[2], cross[3]]); }
    }
  }
  if (!segs.length) return pts;

  // Stitch segments into the longest closed loop (endpoints match exactly since
  // they come from shared cell edges, so a rounded key links them reliably).
  const key = p => `${p.x.toFixed(4)},${p.y.toFixed(4)}`;
  const adj = new Map();
  for (const [a, b] of segs) {
    if (!adj.has(key(a))) adj.set(key(a), []);
    if (!adj.has(key(b))) adj.set(key(b), []);
    adj.get(key(a)).push(b); adj.get(key(b)).push(a);
  }
  let best = [];
  const seen = new Set();
  for (const [k0] of adj) {
    if (seen.has(k0)) continue;
    const startPt = segs.flat().find(p => key(p) === k0);
    const loop = [startPt];
    seen.add(k0);
    let cur = startPt, prevKey = null;
    for (let guard = 0; guard < segs.length * 2 + 8; guard++) {
      const nbrs = adj.get(key(cur)) || [];
      const next = nbrs.find(n => key(n) !== prevKey);
      if (!next) break;
      if (key(next) === key(startPt)) break;      // closed
      prevKey = key(cur); cur = next;
      seen.add(key(cur));
      loop.push(cur);
    }
    if (loop.length > best.length) best = loop;
  }
  if (best.length < 3) return pts;

  // Thin out near-duplicate and collinear points so the outline stays light.
  const out = [];
  for (const p of best) {
    const q = out[out.length - 1];
    if (!q || Math.hypot(p.x - q.x, p.y - q.y) > 0.06) out.push({ x: p.x, y: p.y, bulge: 0 });
  }
  return out.length >= 3 ? out : pts;
}

// Signed area (shoelace) of the loop's straight polygon — used to detect
// winding so callers can normalise orientation if needed.
export function loopSignedArea(loop) {
  let a = 0;
  for (let i = 0; i < loop.length; i++) {
    const p = loop[i], q = loop[(i + 1) % loop.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}
